import { createSlice } from '@reduxjs/toolkit'
import type {
  RestaurantCancellationHistoryGroup,
  RestaurantOverviewQuery,
  RestaurantOverviewSnapshot,
  RestaurantHighlightRules,
} from '@/models/restaurant-manager'
import { createDefaultRestaurantOverviewQuery } from '@/components/pages/restaurant-manager/helpers'
import { reloadCancellation, reloadOverview } from './restaurant-manager-thunks'

type RestaurantManagerState = {
  query: RestaurantOverviewQuery
  snapshot: RestaurantOverviewSnapshot | null
  isOverviewLoading: boolean
  historyGroups: RestaurantCancellationHistoryGroup[] | null
  highlightRules: RestaurantHighlightRules | null
  isCancellationLoading: boolean
  hasCancellationError: boolean
  /** Race condition 制御用: 最後にディスパッチされたリクエストIDと一致する場合のみ state を更新する */
  latestOverviewRequestId: string | null
  latestCancellationRequestId: string | null
}

export const restaurantManagerSlice = createSlice({
  name: 'restaurantManager',
  initialState: {
    query: createDefaultRestaurantOverviewQuery(),
    snapshot: null,
    isOverviewLoading: true,
    historyGroups: null,
    highlightRules: null,
    isCancellationLoading: false,
    hasCancellationError: false,
    latestOverviewRequestId: null,
    latestCancellationRequestId: null,
  } as RestaurantManagerState,
  reducers: {
    setQuery: (state, action: { payload: RestaurantOverviewQuery }) => {
      state.query = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(reloadOverview.pending, (state, action) => {
        state.isOverviewLoading = true
        state.latestOverviewRequestId = action.meta.requestId
      })
      .addCase(reloadOverview.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestOverviewRequestId) return
        state.snapshot = action.payload
        state.isOverviewLoading = false
      })
      .addCase(reloadOverview.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestOverviewRequestId) return
        state.isOverviewLoading = false
      })
      .addCase(reloadCancellation.pending, (state, action) => {
        state.isCancellationLoading = true
        state.hasCancellationError = false
        state.historyGroups = null
        state.highlightRules = null
        state.latestCancellationRequestId = action.meta.requestId
      })
      .addCase(reloadCancellation.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestCancellationRequestId) return
        state.historyGroups = action.payload.cancellationHistoriesByDate
        state.highlightRules = action.payload.highlightRules
        state.isCancellationLoading = false
      })
      .addCase(reloadCancellation.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestCancellationRequestId) return
        state.historyGroups = null
        state.hasCancellationError = true
        state.isCancellationLoading = false
      })
  },
})

export const { setQuery } = restaurantManagerSlice.actions
export const restaurantManagerReducer = restaurantManagerSlice.reducer
